import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { Controls, ControlsButton } from '@/components/Controls';
import InfiniteLoader from '@/components/InfiniteLoader';
import { SectionHeading, SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import SearchBar from '@/components/SearchBar';
import { Table, TableContent, TableHeader, Th } from '@/components/Table';
import useDebounce from '@/hooks/useDebounce';
import useHasPermission from '@/hooks/useHasPermission';
import {
  FreelancerInvoiceSubmissionResource,
  PaginatedResponse,
} from '@/types/resources';
import { api } from '@/utils/api';
import { InvoiceGenerationStatus } from '@/utils/enums';
import { RootState } from '@/utils/store';

import FreelancersInvoicesApprovalTableRow from './FreelancersInvoicesApproveTableRow';
import InvoiceApprovalsFilter from './InvoiceApprovalsFilter';

const tabs = [
  {
    values: [
      InvoiceGenerationStatus.Pending,
      InvoiceGenerationStatus.Needs_Action,
      InvoiceGenerationStatus.Finances_Rejected,
    ],
    title: 'Pending',
  },
  { values: [InvoiceGenerationStatus.Approved], title: 'Approved' },
  { values: [InvoiceGenerationStatus.Rejected], title: 'Rejected' },
  { values: Object.values(InvoiceGenerationStatus), title: 'All' },
];

const StyledTable = styled(Table)`
  border-collapse: separate;
  border-spacing: 0;
  th {
    font-size: 11px;
  }
  th,
  td {
    padding: 19px 16px;
    font-size: 12px;
    font-weight: 700;
    &:last-child {
      width: 56px;
      min-width: 56px;
      height: 64px;
      position: sticky;
      right: 0;
      top: 0;
      bottom: 0;
      z-index: 1;
    }
  }
  td,
  th {
    &:nth-child(1) {
      min-width: 120px;
      padding-left: 24px;
    }
    &:nth-child(2) {
      min-width: 220px;
    }
  }
`;

const StyledTableHeader = styled(TableHeader)`
  position: relative;
`;

const CommentTh = styled(Th)`
  width: 140px;
  min-width: 140px;
`;

const InvoiceIdTh = styled(Th)`
  width: 138px;
  min-width: 138px;
`;

const TeamLeadTh = styled(Th)`
  width: 180px;
  min-width: 180px;
`;

const StatusTh = styled(Th)`
  position: sticky;
  box-shadow: -1px 0 0 ${({ theme }) => theme.colors.grayscale};
  background: ${({ theme }) => theme.colors.white};
  right: 56px;
  top: 0;
  bottom: 0;
  z-index: 1;
`;

const FreelancersInvoicesApprovalTable = () => {
  const [activeStatus, setActiveStatus] = useState(tabs[0]);
  const [search, setSearch] = useState<string>('');
  const [selectedTeamLeads, setSelectedTeamLeads] = useState<number[]>([]);

  const currentUserId = useSelector(
    (rootState: RootState) => rootState.auth.user!.id,
  );

  const canViewAll = useHasPermission('view all invoices');
  // For these users the backend already limits the list to their own, team-lead
  // and assigned company / department submissions; forcing the team lead filter
  // to themselves would hide the company / department ones.
  const canViewScoped = useHasPermission(
    'view companies invoices',
    'view departments invoices',
  );
  const canViewOthers = canViewAll || canViewScoped;
  const debouncedSearch = useDebounce(search, 300);

  const fetchSubmissions = ({
    pageParam = '/v1/payments/freelancer-invoice-submissions',
  }) => {
    return api.get<
      never,
      PaginatedResponse<FreelancerInvoiceSubmissionResource>
    >(pageParam, {
      params: {
        'filter[status]': activeStatus.values,
        ...(canViewOthers
          ? selectedTeamLeads.length > 0 && {
              'filter[team_lead_id]': selectedTeamLeads,
            }
          : { 'filter[team_lead_id]': currentUserId }),
        ...(!!debouncedSearch && {
          'filter[search]': debouncedSearch,
        }),
      },
    });
  };

  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<FreelancerInvoiceSubmissionResource>>(
    [
      'freelancer_invoice_submissions_approval',
      {
        status: activeStatus.values,
        scope: canViewAll ? 'all' : canViewScoped ? 'scoped' : currentUserId,
        ...(selectedTeamLeads.length > 0 && { teamLeads: selectedTeamLeads }),
        ...(!!debouncedSearch && {
          search: debouncedSearch,
        }),
      },
    ],
    fetchSubmissions,
    {
      getNextPageParam: (lastPage) => lastPage.links.next,
    },
  );

  const submissions = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<
        FreelancerInvoiceSubmissionResource[]
      >((acc, currKey: any) => [...acc, ...data!.pages[currKey].data], []),
    [data],
  );

  return (
    <>
      <StyledTableHeader>
        <Controls>
          {tabs.map((tab, index) => (
            <ControlsButton
              key={index}
              active={tab.title === activeStatus.title}
              onClick={() => setActiveStatus(tab)}
            >
              {tab.title}
            </ControlsButton>
          ))}
        </Controls>
        {canViewOthers && (
          <InvoiceApprovalsFilter
            selectedTeamLeads={selectedTeamLeads}
            setSelectedTeamLeads={setSelectedTeamLeads}
          />
        )}
      </StyledTableHeader>
      <SearchBar
        placeholder="Search by name or email"
        value={search}
        setValue={setSearch}
      />
      <SectionHeading>{activeStatus.title} submissions</SectionHeading>
      <ScrollableTable>
        <StyledTable>
          <thead>
            <tr>
              <Th>Request Date</Th>
              <Th>Receiver</Th>
              <Th>Company</Th>
              {canViewOthers && <TeamLeadTh>Team Lead</TeamLeadTh>}
              <Th>Amount</Th>
              <Th>Bonus</Th>
              <Th>VAT</Th>
              <Th>Total</Th>
              <Th>Hours</Th>
              <CommentTh>Comment</CommentTh>
              {activeStatus.values.includes(
                InvoiceGenerationStatus.Approved,
              ) && <InvoiceIdTh>Invoice ID</InvoiceIdTh>}
              <Th>Payment Purpose</Th>
              <Th>Invoice</Th>
              {activeStatus.values.includes(
                InvoiceGenerationStatus.Rejected,
              ) && <Th>Rejection Reason</Th>}
              {activeStatus.values.includes(
                InvoiceGenerationStatus.Pending,
              ) && <CommentTh>Team Lead Comment</CommentTh>}
              <StatusTh>Status</StatusTh>
              <Th>
                {activeStatus.values.includes(
                  InvoiceGenerationStatus.Pending,
                ) && <SrOnly>Actions</SrOnly>}
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!submissions && submissions.length > 0}
            >
              {submissions.map((submission) => (
                <FreelancersInvoicesApprovalTableRow
                  key={submission.id}
                  submission={submission}
                  showTeamLead={canViewOthers}
                  showInvoiceNumber={activeStatus.values.includes(
                    InvoiceGenerationStatus.Approved,
                  )}
                  showRejectionReason={activeStatus.values.includes(
                    InvoiceGenerationStatus.Rejected,
                  )}
                  showCommentColumns={activeStatus.values.includes(
                    InvoiceGenerationStatus.Pending,
                  )}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default FreelancersInvoicesApprovalTable;
